import { HelpFactoryComponent, Modal, ModalConfirmation, NotificationStatus } from '@/components/common';
import { getPermissionErrorMessage, SubPermissions, User } from '@/model/AccountModel';
import { PersonGroupModel, TreeViewPersonGroupModel } from '@/model/EnrollmentModel';
import { useStoreDispatch, useStoreSelector } from '@/store';
import { selectPersonGroups } from '@/store/enrollment/selectors';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Empty, Input, notification, Spin, Tree, TreeDataNode } from 'antd';
import cx from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { AddOrEditPersonGroupModal } from './AddPersonGroupModal';

import { enrollmentApi } from '@/api';
import { handleResponse, removeCommasAndPercentSign } from '@/Helper';
import { HelpFeature } from '@/model/HelpModel';
import { setPersonGroupsAndFolders } from '@/store/enrollment/actions';
import styles from './personGroupsModal.module.scss';

type Props = {
	setShowModal: (flag: boolean) => void;
};

export enum ErrorType {
	None = 0,
	Name = 1,
}

export type AddPersonGroupModalData = {
	isFolder: boolean;
	personGroup: PersonGroupModelWithKey;
	error: ErrorType;
	errorMessage: string;
};

export type PersonGroupModelWithKey = {
	parentKey: React.Key;
} & Omit<PersonGroupModel, 'Selected'>;

type TreeViewPersonGroupModelWithKey = {
	key?: React.Key;
} & TreeViewPersonGroupModel;

type OnSearchInfoType = {
	source: 'clear' | 'input';
};

const { Search } = Input;

const user: User = getUser();
const personGroupPermission = User.getSubComponentPermission(user, SubPermissions.EnrollmentManager_EnrollmentManager_ManagePersonGroups);
const personFolderPermission = User.getSubComponentPermission(user, SubPermissions.EnrollmentManager_EnrollmentManager_ManagePersonFolders);
const defaultAddType: AddPersonGroupModalData = {
	error: ErrorType.None,
	isFolder: false,
	personGroup: null,
	errorMessage: null,
};

const topicId: string = 'PersonGroups';

const PersonGroupsModal: FC<Props> = ({ setShowModal }) => {
	// #region state management variables
	const dispatch = useStoreDispatch();
	const personGroups: TreeViewPersonGroupModel[] = useStoreSelector<TreeViewPersonGroupModel[]>(selectPersonGroups);
	const [showAddPersonGroupModal, setShowAddPersonGroupModal] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<string>('');
	const [addData, setAddData] = useState<AddPersonGroupModalData>({ ...defaultAddType });
	const [loading, setLoading] = useState<boolean>(false);
	const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
	const [treeDataNode, setTreeDataNode] = useState<TreeDataNode[]>([]);
	const [loadingAddModal, setLoadingAddModal] = useState<boolean>(false);
	// #endregion

	useEffect(() => {
		retrivePersonGroupsAndFolders();
	}, []);

	useEffect(() => {
		const copy: TreeViewPersonGroupModel[] = JSON.parse(JSON.stringify(personGroups.filter(pg => pg.GroupId !== -1 && pg.GroupId !== 1)));
		setTreeDataNode(buildTreeDataNode(copy, true));
	}, [personGroups, searchValue]);

	useEffect(() => {
		if (searchValue) {
			const allKeys = getAllKeys(treeDataNode);
			setExpandedKeys(allKeys);
		}
	}, [searchValue, treeDataNode]);

	const retrivePersonGroupsAndFolders = () => {
		if (loading) {
			setLoading(true);
		}
		enrollmentApi
			.getPersonGroupsAndFolders()
			.then(response => {
				if (!handleResponse(response)) {
					dispatch(setPersonGroupsAndFolders([{ FolderId: -1, GroupId: -1, Name: 'All available groups', ChildGroups: [] }, ...response.Entity]));
				} else {
					showGenericErrorNotification();
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const buildTreeDataNode = (
		pgs: TreeViewPersonGroupModelWithKey[],
		isFirstIteration: boolean = false,
		parentNode?: TreeViewPersonGroupModelWithKey
	): TreeDataNode[] => {
		const treeNodes: TreeDataNode[] = [];

		pgs.forEach(pg => {
			const childNodes = buildTreeDataNode(pg.ChildGroups, false, pg);

			if (parentNode?.GroupId) {
				pg.GroupId = parentNode.GroupId;
			}

			const key: string = `${pg.GroupId}-${pg.FolderId}`;

			let idx = -1;
			let nodeTitle: JSX.Element = <>{pg.Name}</>;
			if (searchValue) {
				idx = removeCommasAndPercentSign(pg.Name.toLowerCase()).indexOf(removeCommasAndPercentSign(searchValue.toLowerCase()));
				if (idx > -1) {
					const tempValue = idx + searchValue.length;
					const beforeStr = pg.Name.substring(0, idx);
					const matchedValue = pg.Name.substring(idx, tempValue);
					const afterStr = pg.Name.slice(tempValue);
					nodeTitle = (
						<>
							{beforeStr}
							<span className={styles.matchString}>{matchedValue}</span>
							{afterStr}
						</>
					);
				}
			}

			const nodeMatches = idx > -1;
			const shouldFilterOut = searchValue && !nodeMatches && childNodes.length === 0;

			if (!shouldFilterOut) {
				pg.key = key;
				treeNodes.push({
					key,
					className: isFirstIteration || pg.FolderId === 0 ? styles.rootNode : '',
					title: (
						<div className={cx(styles.treeNodeTitleContainer, styles.odd)}>
							<span className={styles.treeNodeTitle}>{nodeTitle}</span>
							{getActionButtons({
								FolderName: !isFirstIteration ? pg.Name : null,
								GroupName: isFirstIteration ? pg.Name : null,
								ParentFolderId: parentNode?.FolderId ? parentNode.FolderId : 0,
								PersonFolderId: pg.FolderId,
								PersonGroupId: pg.GroupId,
								parentKey: key,
							})}
						</div>
					),
					children: buildTreeDataNode(pg.ChildGroups, false, pg),
				});
			}
		});

		return treeNodes;
	};

	const getAllKeys = (nodes: TreeDataNode[]): React.Key[] => {
		let keys: React.Key[] = [];
		nodes.forEach(node => {
			if (node.children && node.children.length > 0) {
				keys.push(node.key);
				keys = keys.concat(getAllKeys(node.children));
			}
		});
		return keys;
	};

	const getActionButtons = (personGroup: PersonGroupModelWithKey): JSX.Element => {
		if (personGroup.PersonGroupId === 1) {
			return null;
		}

		const disableAddButton: boolean = !personFolderPermission.allowed;
		const disableRenameAndDeleteButtons: boolean =
			(personGroup.PersonFolderId > 0 && !personFolderPermission.allowed) || (personGroup.PersonFolderId === 0 && !personGroupPermission.allowed);

		return (
			<div className={styles.actionButtonsContainer}>
				<Button
					className={styles.actionButton}
					icon={<PlusOutlined />}
					onClick={() =>
						handleShowAddModal(true, {
							...personGroup,
							FolderName: null,
							GroupName: null,
							PersonFolderId: 0,
							ParentFolderId: personGroup.PersonFolderId,
						})
					}
					shape='circle'
					type='primary'
					disabled={disableAddButton}
					title={getPermissionErrorMessage(!disableAddButton)}
				/>
				<Button
					className={styles.actionButton}
					icon={<EditOutlined />}
					onClick={() => handleShowAddModal(personGroup.PersonFolderId ? true : false, personGroup)}
					shape='circle'
					type='primary'
					disabled={disableRenameAndDeleteButtons}
					title={getPermissionErrorMessage(!disableRenameAndDeleteButtons)}
				/>
				<Button
					className={styles.actionButton}
					icon={<DeleteOutlined />}
					onClick={() => handleDeletePersonGroup(personGroup)}
					shape='circle'
					type='primary'
					disabled={disableRenameAndDeleteButtons}
					title={getPermissionErrorMessage(!disableRenameAndDeleteButtons)}
				/>
			</div>
		);
	};

	const onSearch = (value: string, e: React.ChangeEvent<HTMLInputElement>, { source }: OnSearchInfoType) => {
		setSearchValue(value?.trim());
		if (source === 'clear') {
			setExpandedKeys([]);
		}
	};

	const onCancel = () => {
		setShowModal(false);
	};

	const handleDeletePersonGroup = async (personGroup: Omit<PersonGroupModel, 'Selected'>) => {
		ModalConfirmation({
			content: (
				<div>
					<p>
						{_('AreYouSureYouWantToDelete')} <strong>{personGroup.FolderName || personGroup.GroupName}</strong>?
					</p>
				</div>
			),
			onConfirm: () => {
				handleDeletePersonGroupOk(personGroup);
			},
		});
	};

	const showGenericErrorNotification = () => {
		notification['error']({
			message: _('AnErrorHasOcurred'),
		});
	};

	const handleDeletePersonGroupOk = async (personGroup: Omit<PersonGroupModel, 'Selected'>) => {
		try {
			setLoading(true);
			const responseData = await enrollmentApi.deletePersonGroup(personGroup as PersonGroupModel);
			NotificationStatus({
				responseData,
				notUseDefaultNotification: true,
				onSuccessCallback: () => {
					notification['success']({
						message: _('PersonGroupDeletedSuccessfully'),
					});
					setExpandedKeys(prevExpandedKeys => prevExpandedKeys.filter(k => k !== `${personGroup.PersonGroupId}-${personGroup.PersonFolderId}`));
				},
				onFailedValidation: () => {
					notification['error']({
						message: responseData.ErrorMessage,
					});
				},
				onSystemErrorCallback: () => {
					showGenericErrorNotification();
				},
				onPermissionErrorCallback: () => {
					notification['error']({
						message: _('ResponseStatusCode_2'),
					});
				},
			});
		} catch {
			showGenericErrorNotification();
		} finally {
			retrivePersonGroupsAndFolders();
		}
	};

	const handleShowAddModal = (isFolder: boolean, personGroup?: PersonGroupModelWithKey) => {
		batch(() => {
			setShowAddPersonGroupModal(true);
			setAddData({
				isFolder,
				personGroup,
				error: ErrorType.None,
				errorMessage: null,
			});
		});
	};

	const onAddModalSave = async (personGroup: PersonGroupModelWithKey) => {
		try {
			const responseData = await enrollmentApi.savePersonGroup(personGroup);
			NotificationStatus({
				responseData,
				notUseDefaultNotification: true,
				onFailedValidation: () => {
					setAddData({
						...addData,
						personGroup: personGroup,
						error: ErrorType.Name,
						errorMessage: responseData.ErrorMessage,
					});
				},
				onSystemErrorCallback: () => {
					showGenericErrorNotification();
					batch(() => {
						setShowAddPersonGroupModal(false);
						setAddData({ ...defaultAddType });
					});
				},
				onSuccessCallback: () => {
					notification['success']({
						message: _('PersonGroupSavedSuccessfully'),
					});

					batch(() => {
						setShowAddPersonGroupModal(false);
						setAddData({ ...defaultAddType });
						if (personGroup.FolderName) {
							setExpandedKeys([...expandedKeys, personGroup.parentKey]);
						}
					});
				},
				onPermissionErrorCallback: () => {
					notification['error']({
						message: _('ResponseStatusCode_2'),
					});
					setLoadingForAddModal(false);
				},
			});
		} catch {
			showGenericErrorNotification();
		} finally {
			retrivePersonGroupsAndFolders();
		}
	};

	const handleOnExpand = (newExpandedKeys: React.Key[]) => {
		setExpandedKeys(newExpandedKeys);
	};

	const setLoadingForAddModal = (load: boolean) => {
		setLoadingAddModal(load);
	};

	const hideTree = treeDataNode?.length === 0 && searchValue.length > 0;

	return (
		<>
			<Modal
				visible
				title={_('PersonGroups')}
				onCancel={onCancel}
				addHelpButton={<HelpFactoryComponent helpFeature={HelpFeature.Enrollment} topicId={topicId} className={styles.helpButton} />}>
				<Spin spinning={loading} tip={_('loading')}>
					<div
						className={cx(styles.personGroupsContainer, {
							[styles.personGroupsContainerEmpty]: hideTree,
						})}>
						<div className={styles.header}>
							<Search allowClear enterButton onSearch={onSearch} />
							<Button
								htmlType='button'
								type='primary'
								onClick={() => handleShowAddModal(false, null)}
								disabled={!personGroupPermission.allowed}
								title={getPermissionErrorMessage(personGroupPermission.allowed)}>
								<PlusOutlined />
								{`Add New Person ${_('Group')}`}
							</Button>
						</div>
						<div className={styles.tableHeader}>
							<div className={styles.emptyCell} />
							<span className={styles.separator}>|</span>
							<div className={styles.name}>
								<span>{_('Name')}</span>
							</div>
						</div>
						{hideTree ? (
							<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
						) : (
							<div className={styles.tableBody}>
								<Tree
									autoExpandParent={false}
									expandedKeys={expandedKeys}
									onExpand={handleOnExpand}
									selectable={false}
									showLine
									showIcon={false}
									treeData={treeDataNode}
									motion={false}
								/>
							</div>
						)}
					</div>
				</Spin>
			</Modal>
			{showAddPersonGroupModal && (
				<AddOrEditPersonGroupModal
					data={addData}
					savePersonGroup={onAddModalSave}
					setAddData={setAddData}
					setShowModal={setShowAddPersonGroupModal}
					loading={loadingAddModal}
					setLoading={setLoadingForAddModal}
				/>
			)}
		</>
	);
};

export { PersonGroupsModal };
